import * as React from 'react';
import { useState } from 'react';
import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getFullName } from '@klareospace/pharos-lib-utils';
import { ORDER_UPDATE_FIELD_CATEGORY, type Order } from '@klareospace/pharos-lib-types';
import { DrawerFormSkeleton, DrawerSection } from '@klareospace/pharos-lib-web-utils';

import { useOrderEditability } from '../hooks/use-order-editability';
import { CarrierInlineEditor } from './carrier-inline-editor';
import { TimeSlotField } from './time-slot-field';
import { TimeWindowEditDialog } from './time-window-edit-dialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrderDetailSectionProps {
  readonly isLoading: boolean;
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function OrderDetailSection({ isLoading, order }: OrderDetailSectionProps): React.JSX.Element {
  const { t } = useTranslation('orders');
  const { getFieldState, lockReason } = useOrderEditability(order);
  const [isTimeWindowDialogOpen, setIsTimeWindowDialogOpen] = useState(false);

  if (isLoading) {
    return <DrawerFormSkeleton fieldCount={2} />;
  }

  const customerName = getFullName(order.customer.firstName, order.customer.lastName);
  const timeWindowFieldState = getFieldState(ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING);

  return (
    <>
      <DrawerSection
        icon={Package}
        rows={[
          {
            cells: [
              {
                cell: <span className="text-sm text-foreground">{order.orderType.label}</span>,
                label: t('drawer.orderType'),
              },
              {
                cell: <span className="text-sm capitalize text-foreground">{order.transportType}</span>,
                label: t('drawer.transportType'),
              },
            ],
          },
          {
            cells: [
              {
                cell: (
                  <TimeSlotField
                    fieldState={timeWindowFieldState}
                    lockReason={lockReason}
                    onEdit={(): void => setIsTimeWindowDialogOpen(true)}
                    timeSlot={order.requestedTimeWindow}
                    title={t('drawer.requestedTimewindow')}
                  />
                ),
                label: t('drawer.requestedTimewindow'),
              },
              {
                cell: <CarrierInlineEditor fieldState={timeWindowFieldState} lockReason={lockReason} order={order} />,
                label: t('drawer.requestedCarrier'),
              },
            ],
          },
        ]}
        title={t('drawer.orderDetail')}
        titleSuffix={
          <span className="font-normal text-muted-foreground">
            {t('drawer.placedBy')} {customerName}
          </span>
        }
      />
      {isTimeWindowDialogOpen && (
        <TimeWindowEditDialog
          onClose={(): void => setIsTimeWindowDialogOpen(false)}
          open
          order={order}
          target="requestedTimeWindow"
        />
      )}
    </>
  );
}
